import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { CartCourse, CartContextValue } from './types';

const TAX_RATE = 0.1;
const STORAGE_KEY_PREFIX = 'centrix_cart';

function storageKey(userId: string | undefined): string | null {
  return userId ? `${STORAGE_KEY_PREFIX}_${userId}` : null;
}

function readFromStorage(userId: string | undefined): CartCourse[] {
  const key = storageKey(userId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartCourse[]) : [];
  } catch {
    return [];
  }
}

function writeToStorage(userId: string | undefined, items: CartCourse[]): void {
  const key = storageKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // Storage quota exceeded or unavailable
  }
}

const CartContext = createContext<CartContextValue | null>(null);

interface CartProviderProps {
  children: ReactNode;
  userId: string;
}

export function CartProvider({ children, userId }: CartProviderProps) {
  const [items, setItems] = useState<CartCourse[]>(() => readFromStorage(userId));

  // Reload from storage when the authenticated user changes
  useEffect(() => {
    setItems(readFromStorage(userId));
  }, [userId]);

  useEffect(() => {
    writeToStorage(userId, items);
  }, [userId, items]);

  const subtotalPence = useMemo(
    () => items.reduce((sum, item) => sum + item.pricePence, 0),
    [items]
  );
  const taxesPence = useMemo(() => Math.round(subtotalPence * TAX_RATE), [subtotalPence]);
  const totalPence = useMemo(() => subtotalPence + taxesPence, [subtotalPence, taxesPence]);

  const addItem = useCallback((course: CartCourse) => {
    setItems((prev) => {
      const exists = prev.some((item) => item.id === course.id);
      return exists ? prev : [...prev, course];
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo<CartContextValue>(
    () => ({
      items,
      itemCount: items.length,
      isLoading: false,
      subtotalPence,
      taxesPence,
      totalPence,
      addItem,
      removeItem,
      clearCart,
    }),
    [items, subtotalPence, taxesPence, totalPence, addItem, removeItem, clearCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}
